/*
 * stats.js -- pocitani cisel dlazdic „stats" nahoru (plan 08-08, KOMP-02).
 *
 * CO TENHLE MODUL NENI: NOSITEL OBSAHU.
 *
 * `stats.php` vypisuje ROVNOU CILOVE cislo. Bez skriptu, s omezenym
 * pohybem i v tisku je tedy videt spravna hodnota. Zmrazeny mockup ma
 * v markupu nulu a cilovou hodnotu dopisuje skript (`navrh-th-hp.html`
 * radek 907) -- to je past, kterou tenhle modul zamerne NEkopiruje: bez
 * JS by web tvrdil „0 let zkusenosti", tedy nepravdu o firme (kontrakt B).
 *
 * MODUL PROTO DELA JEDINOU VEC: kdyz se dlazdice objevi ve viewportu,
 * SROVNA cislo na nulu a behem 900 ms ho dopocita zpatky na cilovou
 * hodnotu z `data-count`. Vynechani teto animace nema zadny dopad na
 * obsah.
 *
 * KDY SE ANIMACE NESPUSTI VUBEC:
 *   - `prefers-reduced-motion: reduce` -- uzivatel si pohyb odmitl
 *     (POKYNY §5b). Cislo zustane cilove od prvniho snimku.
 *   - `IntersectionObserver` neni k dispozici -- degradace zni „vse
 *     viditelne" (kontrakt B), tedy cisla rovnou cilova. ZADNY
 *     nahradni scroll posluchac: byl by to druhy mechanismus, ktery
 *     nikdo nezmeri.
 *
 * ANIMUJE SE textContent, NE CSS. Kontrakt povoluje animovat jen
 * `opacity` a `transform`, ale tohle zadna CSS animace neni -- je to
 * postupna zmena TEXTU, tedy vlastnost obsahu. Layout se pritom nehybe:
 * cislo ma v CSS `font-variant-numeric: tabular-nums`, takze vsechny
 * cislice maji stejnou sirku a mezikrok „7" nezmeni sirku dlazdice.
 *
 * ODDELOVAC TISICU SE NEHADA. Cilova hodnota muze byt „120 000"; modul
 * si z ni precte, JAKY znak oddeluje trojice, a mezikroky formatuje
 * TIMTEZ znakem. Zadny `toLocaleString()`: ten zavisi na jazyku
 * prohlizece, takze cesky web na anglickem systemu by uprostred
 * animace ukazal „120,000" a na konci „120 000".
 *
 * BEZPECNOST (threat model 08-08): zadny `innerHTML`, zadny `eval`,
 * zadna interpolace vstupu -- modul zapisuje vyhradne `textContent`
 * slozeny z cislic a jednoho oddelovace precteneho z DOM.
 */

import { guard } from './resize-guard.js';

/** Trida na <html>: "stats modul dobehl". */
const READY_CLASS = 'stats-ready';

/** Delka dopocitani jedne dlazdice (ms). */
const DURATION = 900;

/** Volba uzivatele "omezit pohyb". */
const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Bezpecne prevede NodeList na staticke pole.
 *
 * @param {string} selector CSS selektor.
 * @return {Array<Element>}
 */
function all(selector) {
	return Array.prototype.slice.call(document.querySelectorAll(selector));
}

/**
 * Naformatuje cislo TYMZ oddelovacem trojic, jaky ma cilova hodnota.
 *
 * @param {number} value Mezikrok.
 * @param {string} separator Oddelovac ('' = zadny).
 * @return {string}
 */
function group(value, separator) {
	const digits = String(value);

	if (!separator) {
		return digits;
	}

	let out = '';

	for (let i = 0; i < digits.length; i++) {
		if (i > 0 && (digits.length - i) % 3 === 0) {
			out += separator;
		}

		out += digits.charAt(i);
	}

	return out;
}

/**
 * Dopocita jedno cislo z nuly na cilovou hodnotu.
 *
 * @param {Element} el Prvek `.stat-value`.
 * @param {number} target Cilova hodnota.
 * @param {string} separator Oddelovac trojic.
 * @return {void}
 */
function countUp(el, target, separator) {
	const started = Date.now();

	el.textContent = group(0, separator);

	const tick = function () {
		const progress = Math.min(1, (Date.now() - started) / DURATION);

		/* Zpomaleni na konci (ease-out) -- bez knihovny, jednim vzorcem. */
		const eased = 1 - Math.pow(1 - progress, 3);

		el.textContent = group(Math.round(target * eased), separator);

		if (progress < 1) {
			window.requestAnimationFrame(tick);
		} else {
			/*
			 * Posledni snimek se zapisuje z CILE, ne z vypoctu: zaokrouhleni
			 * by u „120 000" mohlo skoncit o jednicku vedle a na strance by
			 * navzdy zustalo spatne cislo.
			 */
			el.textContent = group(target, separator);
		}
	};

	window.requestAnimationFrame(tick);
}

/**
 * Spusti modul dlazdic s cisly.
 *
 * @return {void}
 */
export function init() {
	const values = all('.stat-num .stat-value');

	if (!values.length) {
		return;
	}

	const reduced = !!(window.matchMedia && window.matchMedia(MOTION_QUERY).matches);

	/*
	 * Bez IntersectionObserveru se NIC nedeje -- cisla uz v DOM cilova
	 * jsou. Tohle je cela degradace, ne jeji zacatek.
	 */
	if (reduced || typeof window.IntersectionObserver !== 'function') {
		document.documentElement.classList.add(READY_CLASS);
		return;
	}

	const observer = new window.IntersectionObserver(function (entries) {
		entries.forEach(function (entry) {
			if (!entry.isIntersecting) {
				return;
			}

			const el = entry.target;

			observer.unobserve(el);

			const raw = (el.parentNode.getAttribute('data-count') || '').trim();
			const digits = raw.replace(/\D+/g, '');

			if (!digits) {
				return;
			}

			const match = raw.match(/\d(\D)\d/);

			// Po zmene vysky viewportu (iOS lista) pocka, az se ustali
			// (resize-guard.js) -- prepis textu v okne tapu by zahodil klik.
			guard(function () {
				countUp(el, parseInt(digits, 10), match ? match[1] : '');
			});
		});
	}, { threshold: 0.4 });

	values.forEach(function (el) {
		observer.observe(el);
	});

	document.documentElement.classList.add(READY_CLASS);
}
